'use client';

import { PopoverOrigin } from '@mui/material/Popover';
import { clsx } from 'clsx';
import { FC, useEffect, useState } from 'react';
import { BarePopover } from 'atoms/BarePopover';
import { Icon, IconType } from 'atoms/Icon';
import { Title } from 'atoms/Typography/Typography';
import { TickIcon } from 'molecules/TickIcon';
import { PopoverProps } from 'modules/mui/types';
import { NOTIFICATION_SHOW_TIME } from 'modules/shop/notificationManager/constants';
import styles from './Notification.module.css';

export const Notification: FC<
  PopoverProps & {
    id: string;
    variant?: 'default' | 'dark' | 'base' | 'default-light';
  }
> = ({ children, id, variant = 'default', ...props }) => {
  const [timeout, setTimeoutState] = useState<NodeJS.Timeout>();

  const close = () => props.onClose?.({}, 'backdropClick');

  useEffect(() => {
    if (props.open) {
      const t = setTimeout(() => {
        close();
      }, NOTIFICATION_SHOW_TIME);
      setTimeoutState(t);

      return () => {
        clearTimeout(t);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const transformOrigin: PopoverOrigin = variant.includes('default')
    ? {
        vertical: 'top',
        horizontal: 'right',
      }
    : {
        vertical: 'bottom',
        horizontal: 'left',
      };

  const anchorOrigin: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: variant.includes('default') ? 'right' : 'center',
  };

  return (
    <BarePopover
      slotProps={{
        paper: {
          onMouseEnter: () => clearTimeout(timeout),
          onMouseLeave: close,
        },
      }}
      marginThreshold={0}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      {...props}
      classes={{
        paper: clsx(styles.root, variant !== 'default' && styles[variant]),
        root: styles.backdrop,
      }}
      disableEnforceFocus
      disableRestoreFocus
    >
      {variant.includes('default') && (
        <TickIcon
          color={variant === 'default-light' ? 'gray' : 'green'}
          className={styles.icon}
        />
      )}
      <Title type="md" className={styles.content}>
        {children}
      </Title>
      <Icon
        type={IconType.close}
        className={clsx(styles.close, variant !== 'default' && styles[variant])}
        onClick={close}
      />
    </BarePopover>
  );
};
